<template>
  <nav
    :class="
      is_active
        ? 'translate-x-0 '
        : 'rtl:translate-x-full ltr:-translate-x-full'
    "
    class="fixed z-20 flex flex-col h-screen transition-transform duration-150 ease-in transform bg-white shadow-xl ltr:left-0 rtl:right-0 text-primaryDark lg:static shadow-primary/10"
  >
    <div
      class="sticky top-0 z-10 flex items-center justify-between gap-2 px-5 py-2 bg-white border-b border-tertiary shrink-0"
    >
      <router-link to="/" class="shrink-0">
        <Image
          src="/logo.svg"
          class="object-cover h-[56px] w-[56px] mx-auto"
          alt="enjaz"
          loading="lazy"
        />
      </router-link>
      <div class="flex flex-col items-center justify-center min-w-0">
        <h6 class="text-base font-normal truncate lg:text-xl">
          {{ $t("enjaz") }}
        </h6>
        <span class="font-light text-[10px] whitespace-nowrap">{{
          $t("control-panel")
        }}</span>
      </div>
      <button
        class="shrink-0 lg:hidden"
        :aria-label="$t('toggle-sidebar')"
        @click="toggleSidebar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          style="fill: rgba(0, 0, 0, 1)"
        >
          <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"></path>
        </svg>
      </button>
    </div>

    <div class="flex-1 overflow-y-auto hiddenScrollbar">
      <template v-for="(route, index) in menu" :key="index">
        <div v-if="route.children.length" class="w-full mb-1">
          <button
            @click="toggleGroup(index)"
            :class="
              isGroupCurrent(route)
                ? 'border-e-4 border-primary text-primary'
                : 'text-primaryDark'
            "
            class="inline-flex flex-row items-center justify-between w-full h-10 gap-2 px-5 text-sm font-semibold transition-colors"
          >
            <span class="flex items-center min-w-0 gap-2">
              <i :class="route.icon" class="text-xl shrink-0"></i>
              <span class="truncate leading-snug text-start">{{
                route.title
              }}</span>
            </span>
            <i
              class="text-lg transition-transform duration-300 shrink-0 text-primaryDark bx bx-chevron-down"
              :class="{ 'rotate-180': route.is_active }"
            ></i>
          </button>
          <div
            class="grid transition-all duration-300 ease-in-out"
            :class="route.is_active ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
          >
            <ul class="w-full overflow-hidden">
              <li
                class="w-full px-2"
                v-for="child in route.children"
                :key="child.route_name"
              >
                <router-link
                  :to="{
                    name: child.route_name,
                  }"
                  class="flex flex-row items-center w-full h-10 gap-2 px-4 ps-8 text-xs rounded-lg"
                  active-class="font-medium bg-tertiary text-primary"
                >
                  <i :class="child.icon" class="text-lg shrink-0"></i>
                  <span class="truncate leading-snug">{{ child.title }}</span>
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <div class="px-2 mb-1" v-else>
          <router-link
            :to="{
              name: route.route_name,
            }"
            class="flex flex-row items-center w-full h-10 gap-2 px-4 text-sm rounded-lg"
            active-class="font-medium bg-tertiary text-primary"
          >
            <i :class="route.icon" class="text-xl shrink-0"></i>
            <span class="truncate leading-snug">{{ route.title }}</span>
          </router-link>
        </div>
      </template>
    </div>
  </nav>
</template>
<script lang="ts" setup>
import navLinks from "@/core/constant/navLinks";
import useHasRole from "@/core/hooks/useHasRole";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

defineProps<{
  is_active: boolean;
}>();

const emit = defineEmits(["toggleSidebar"]);
const router = useRouter();
const route = useRoute();

const toggleSidebar = () => {
  emit("toggleSidebar");
};

const { has } = useHasRole();

type MenuChild = { route_name: string; title: string; icon: string };
type MenuGroup = {
  route_name: string;
  title: string;
  icon: string;
  children: MenuChild[];
  is_active: boolean;
};

const groupHasRoute = (group: { children: MenuChild[] }, routeName: string) =>
  group.children.some((child) => child.route_name === routeName);

const isGroupCurrent = (group: MenuGroup) =>
  groupHasRoute(group, route.name as string);

const menu = ref<MenuGroup[]>(
  navLinks
    .filter((item) => has(item.allowedPermission))
    .map((item) => ({
      ...item,
      is_active: groupHasRoute(item, route.name as string),
    }))
);

const toggleGroup = (index: number) => {
  const target = menu.value[index];
  const wasActive = target.is_active;
  menu.value.forEach((group) => (group.is_active = false));
  target.is_active = !wasActive;
};

watch(
  () => route.name,
  (routeName) => {
    menu.value.forEach((group) => {
      group.is_active = groupHasRoute(group, routeName as string);
    });
  }
);
</script>
